import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Search } from "lucide-react";
import BracketView from "@/components/BracketView";
import ShareMenu from "@/components/ShareMenu";
import { EventCTA } from "@/components/EventCTA";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicBracket() {
  const { tid } = useParams();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);
  const [group, setGroup] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    axios.get(`${API}/tournaments/${tid}/public-bracket`)
      .then(({ data }) => { setData(data); document.title = `${data.name} — Live Brackets | Pickle Bullpen`; })
      .catch(() => setMissing(true));
  }, [tid]);

  const groups = useMemo(() => {
    if (!data) return [];
    const set = new Map();
    data.matches.forEach((m) => {
      const label = [m.event_type, m.division, m.age_bracket].filter(Boolean).join(" · ") || "Main";
      set.set(label, true);
    });
    return [...set.keys()];
  }, [data]);

  useEffect(() => { if (groups.length && !group) setGroup(groups[0]); }, [groups]); // eslint-disable-line

  if (missing) return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col items-center justify-center gap-3" data-testid="bracket-not-found">
      <p className="uppercase tracking-widest text-zinc-500">Tournament not found</p>
      <Link to="/court-board" className="text-lime-400 text-sm underline">Back to Court Board</Link>
    </div>
  );
  if (!data) return null;

  const q = search.trim().toLowerCase();
  const nameOf = (id) => (data.entries.find((e) => e.id === id) || {}).display_name || "";
  const inGroup = (m) => (([m.event_type, m.division, m.age_bracket].filter(Boolean).join(" · ") || "Main") === group);
  const matchesSearch = (m) => !q || `${nameOf(m.side_a)} ${nameOf(m.side_b)}`.toLowerCase().includes(q);
  const shown = data.matches.filter((m) => inGroup(m) && matchesSearch(m));

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white px-4 sm:px-6 py-8" data-testid="public-bracket-page">
      <div className="max-w-6xl mx-auto">
        <nav className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <Link to={`/court-board/${tid}`} data-testid="bracket-back-board" className="text-xs uppercase tracking-widest text-zinc-500 hover:text-lime-400 transition-colors inline-flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" /> Live Court Board
          </Link>
          <div className="border border-zinc-800 px-3 py-2">
            <ShareMenu url={`${window.location.origin}/brackets/${tid}`}
              text={`📊 Follow the live brackets for ${data.name} on Pickle Bullpen!`}
              testId="bracket-share" label="Share brackets" />
          </div>
        </nav>
        <div className="flex items-center gap-4 mb-1">
          {data.logo && <img src={data.logo} alt={`${data.name} logo`} className="w-14 h-14 object-contain" data-testid="bracket-event-logo" />}
          <h1 className="font-display font-black uppercase leading-tight text-3xl sm:text-4xl" data-testid="bracket-title">{data.name} — Brackets</h1>
        </div>
        <p className="text-zinc-500 text-xs uppercase tracking-widest mb-2">{data.date} · {data.location} · live scores update as they're approved</p>
        {data.roster_public && (
          <Link to={`/rosters/${tid}`} data-testid="bracket-roster-link" className="inline-block text-lime-400 text-xs uppercase tracking-widest font-bold hover:underline mb-4">View Official Roster →</Link>
        )}

        <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="bracket-controls">
          {groups.length > 0 && (
            <select data-testid="bracket-group-select" value={group} onChange={(e) => setGroup(e.target.value)}
              className="bg-zinc-900 border border-zinc-700 px-3 py-2.5 text-sm text-white focus:outline-none focus:border-lime-400">
              {groups.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          )}
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input data-testid="bracket-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search a player or team name…"
              className="w-full bg-zinc-900 border border-zinc-700 pl-9 pr-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-lime-400" />
          </div>
        </div>

        {shown.length === 0 ? (
          <div className="border border-dashed border-zinc-800 p-10 text-center text-zinc-600 text-sm" data-testid="bracket-empty">
            {q ? "No matches found for that name in this division." : "Brackets for this division haven't been generated yet — check back soon."}
          </div>
        ) : (
          <BracketView tournamentId={tid} matches={shown} entries={data.entries} canManage={false} onChange={() => {}} />
        )}
        <EventCTA eventName={data.name} />
      </div>
    </div>
  );
}
